/**
 * orden-pago.ts — APR-7 / APU-13 (openspec/changes/retenciones-en-el-pago).
 * Response contract of `GET /sumi/cuentas-pagar/pagos/:id`: the Orden de Pago behind a `pago` movement, with the
 * cancelado / retenciones / neto breakdown PER comprobante. Money fields are the raw `numeric` string
 * round-trip (never `Number()`'d on the wire). `canceladoArs = round2(canceladoCc × TC)`, retenciones are ARS,
 * `netoArs = canceladoArs − Σ retenciones`. A historic pago (no order) comes back with `ordenPago: null` and no
 * retenciones, exactly as it was printed before.
 *
 * Replaces the local schema the frontend kept for this endpoint.
 */
import { z } from 'zod';

export const OrdenPagoRefSchema = z.object({
  id: z.string().uuid(),
  numero: z.number().int().positive(),
  fechaPago: z.string(),
  medioPago: z.string(),
});
export type OrdenPagoRef = z.infer<typeof OrdenPagoRefSchema>;

export const OrdenPagoComprobanteInfoSchema = z.object({
  id: z.string().uuid(),
  tipo: z.string(),
  letra: z.string().nullable(),
  puntoVenta: z.string().nullable(),
  numero: z.string().nullable(),
  /**
   * Issue date and original amount (in the comprobante's own currency), printed in the order's table like the
   * reference OP. Default null so a SPA deployed before the backend still parses the older response.
   */
  fechaEmision: z.string().nullable().default(null),
  fechaVencimiento: z.string().nullable(),
  montoTotal: z.string().nullable().default(null),
});

export const OrdenPagoRetencionSchema = z.object({
  id: z.string().uuid(),
  tipoRetencionId: z.string().uuid(),
  tipoRetencionNombre: z.string(),
  /** ARS. */
  monto: z.string(),
});
export type OrdenPagoRetencion = z.infer<typeof OrdenPagoRetencionSchema>;

export const OrdenPagoComprobanteSchema = z.object({
  comprobanteId: z.string().uuid(),
  comprobante: OrdenPagoComprobanteInfoSchema.nullable(),
  moneda: z.string(),
  tipoCambio: z.string().nullable(),
  /** Σ imputaciones of the order over the comprobante, in its own currency. */
  canceladoCc: z.string(),
  canceladoArs: z.string(),
  retenciones: z.array(OrdenPagoRetencionSchema),
  netoArs: z.string(),
});
export type OrdenPagoComprobante = z.infer<typeof OrdenPagoComprobanteSchema>;

export const OrdenPagoTotalesSchema = z.object({
  canceladoArs: z.string(),
  retencionesArs: z.string(),
  netoArs: z.string(),
});

export const OrdenPagoDetalleSchema = z.object({
  /** The `pago` movement the URL addresses. */
  movimientoId: z.string().uuid(),
  ordenPago: OrdenPagoRefSchema.nullable(),
  proveedorId: z.string().uuid(),
  proveedorNombre: z.string(),
  proveedorCuit: z.string().nullable(),
  /** `movimiento.monto` as persisted (signed: pago rows are negative). */
  monto: z.string(),
  medioPago: z.string().nullable(),
  createdAt: z.string(),
  comprobantes: z.array(OrdenPagoComprobanteSchema),
  totales: OrdenPagoTotalesSchema,
  /** True once the `pago` has been annulled (the cascade annuls the whole order). */
  anulada: z.boolean(),
  /**
   * Who made the order (its creator, or the user who recorded a historic pago), printed under
   * "Confeccionó" in the signatures. Null when the user cannot be resolved; default null so a SPA
   * deployed before the backend still parses the older response.
   */
  confeccionadoPor: z.string().nullable().default(null),
});
export type OrdenPagoDetalle = z.infer<typeof OrdenPagoDetalleSchema>;
